import {
  X_LABEL_MINUTE_INTERVAL,
  X_AXIS_DIVISIONS,
  X_AXIS_STEP_SIZE,
  DEFAULT_DRIVING_DISTANCE,
  DEFAULT_DRIVING_TIME
} from '@/services/consts/every_24_hours_report';
import { formatDateTimeWithMilliseconds } from '@/services/functions';
import every24HoursPreviewType from "@/types/responses/every24HoursPreview";
import moment from 'moment';

export function createScaleMin(deliveryPlan: every24HoursPreviewType, slideBarValue: number): number {
  const leavingWarehouseMilliseconds = new Date(deliveryPlan.leaving_warehouse).getTime();
  if (!slideBarValue) {
    return leavingWarehouseMilliseconds;
  }

  const returningWarehouseMilliseconds = new Date(deliveryPlan.returning_warehouse).getTime();
  const stepSize = (returningWarehouseMilliseconds - leavingWarehouseMilliseconds) / 100;
  const addMilliseconds = stepSize * slideBarValue;
  const min = leavingWarehouseMilliseconds + addMilliseconds;

  if (min > returningWarehouseMilliseconds) {
    return returningWarehouseMilliseconds;
  }

  return min;
}

export function createMinMaxScaleLabel(fromMilliSeconds: number, outputTime: string): number[] {
  const minuteInterval = X_LABEL_MINUTE_INTERVAL[outputTime];
  const tempDate = new Date(fromMilliSeconds);
  const max = new Date(tempDate.setMinutes(tempDate.getMinutes() + (minuteInterval * X_AXIS_DIVISIONS))).getTime();

  return [fromMilliSeconds, max];
}

export function formatDisplayChart(chartData: {[key: string]: string}[]) {
  const result: [number, number][] = [];

  for (const [dateTime, value] of Object.entries(chartData)) {
    result.push([
      new Date(dateTime).getTime(),
      Number(value)
    ]);
  }

  return result;
}

export function formatDisplayDistanceChart(chartData: {[key: string]: string}[]) {
  const result: [number, number][] = [];

  for (const [dateTime, value] of Object.entries(chartData)) {
    result.push([
      new Date(dateTime).getTime(),
      Number(value) * 1000
    ]);
  }

  return result;
}

export function xLabels(scaleMinMax: number[], outputTime: string) {
  let dateTime = formatDateTimeWithMilliseconds(new Date(scaleMinMax[0]), '/');
  const minuteInterval = X_LABEL_MINUTE_INTERVAL[outputTime];
  let addMinute = minuteInterval;
  const xLabel = [];
  const xLabelCount = 5;

  for(let i = 0; i < xLabelCount; i++) {
    xLabel.push(dateTime);
    const format = i == xLabelCount - 2 ? "yyyy/MM/DD HH:mm:ss" : "HH:mm:ss";
    dateTime = moment(scaleMinMax[0]).add(addMinute, "m").format(format);
    addMinute += minuteInterval;
  }

  return xLabel
}

export function getVerticalLines(scaleMinMax: number[], outputTime: string) {
  const result: {
    xAxis: number | null
  }[] = [];

  const stepSize = X_AXIS_STEP_SIZE[outputTime];

  for (let d = new Date(scaleMinMax[0]); d <= new Date(scaleMinMax[1]); d.setTime(d.getTime() + stepSize)) {
    result.push({
      xAxis: new Date(d).getTime()
    })
  }

  return result;
}

export function getMaxPoint(speeds: number[][]): number[] {
  if (!speeds.length) {
    return [0, 0];
  }
  return speeds.reduce((max, point) =>
    point[1] > max[1] ? point : max
  )
}

export function getDistanceAtOutputTime(
  distanceData: number[][],
  scaleMinMax: number[],
): string {
  const multiple = 100000;

  let distance = 0;
  const startDate = scaleMinMax[0];
  const endDate = scaleMinMax[1];

  for (let i = 0; i < distanceData.length; i++) {
    const [dateTime, dist] = distanceData[i];
    if (dateTime < startDate) {
      continue;
    }
    if (dateTime > endDate) {
      break;
    }
    distance += (dist / 1000) * multiple;
  }

  if (!distance) {
    return DEFAULT_DRIVING_DISTANCE;
  }

  return distance / multiple + "km";
}

export function getDrivingTime(deliveryPlan: every24HoursPreviewType, scaleMinMax: number[]) {
  const leavingWarehouse = new Date(deliveryPlan.leaving_warehouse).getTime();
  const returningWarehouse = new Date(deliveryPlan.returning_warehouse).getTime();
  const startDate = Math.max(scaleMinMax[0], leavingWarehouse);
  const endDate = Math.min(returningWarehouse, scaleMinMax[1]);
  const drivingTimes = getDataFrom(deliveryPlan.devided_driving_section, startDate, endDate);
  let totalDrivingTime = 0;

  if (drivingTimes.length) {
    for (let i = 0; i < drivingTimes.length; i++) {
      let $drivingStartTime = drivingTimes[i]['start'];
      let $drivingEndTime = drivingTimes[i]['end'];
      if ($drivingStartTime >= endDate) {
        break;
      }
      if ($drivingEndTime <= startDate) {
        continue;
      }
      if ($drivingStartTime < startDate) {
        $drivingStartTime = startDate;
      }
      if ($drivingEndTime > endDate) {
        $drivingEndTime = endDate;
      }
      totalDrivingTime += ($drivingEndTime - $drivingStartTime);
    }
  }

  const match = (new Date(totalDrivingTime)).toUTCString().match(/..:..:../);
  return match ? match[0] : DEFAULT_DRIVING_TIME;
}

function getDataFrom(
  drivingTimes: every24HoursPreviewType['devided_driving_section'],
  startDate: number,
  endDate: number
) {
  const drivingTimesBetweenStartEnd = [];

  for (const sectionArray of Object.values(drivingTimes)) {
    for (const section of sectionArray) {
      if (endDate < section.start) {
        break;
      }
      if (startDate > section.end) {
        continue;
      }
      drivingTimesBetweenStartEnd.push(section);
    }
  }

  return drivingTimesBetweenStartEnd;
}

function getSliceIndex(
  data: number[][],
  targetTime: number,
  isStart: boolean
) {
  let low = 0, high = data.length - 1, result = -1
  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const midVal = data[mid][0]

    if (isStart ? midVal >= targetTime : midVal > targetTime) {
      result = mid
      high = mid - 1
    } else {
      low = mid + 1
    }
  }
  return result === -1 ? (isStart ? 0 : data.length) : result
}

export function getDisplayData(
  data: number[][],
  scaleMinMax: number[]
) {
  const startIndex = getSliceIndex(data, scaleMinMax[0], true)
  const endIndex = getSliceIndex(data, scaleMinMax[1], false)

  return data.slice(startIndex, endIndex)
}
